/**
 * Anonymous/visitor program persistence via the `EIFO_PROGRAM` cookie.
 *
 * Mirrors `src/lib/i18n/locale-cookie.ts` exactly — client-safe read/write,
 * with the `next/headers` reader kept in a SEPARATE module. **This file must
 * never import `next/headers`**: it is pulled into client components (the
 * program switcher, the auto-switch banner) and doing so breaks the client
 * bundle.
 *
 * The cookie holds a program SLUG, not a uuid — human-debuggable, and it
 * survives a program being recreated. An unrecognised or inactive slug is
 * treated as ABSENT everywhere (never as an error).
 *
 * NOT HttpOnly by design — the client switcher writes it.
 */
export const PROGRAM_COOKIE = 'EIFO_PROGRAM';

const ONE_YEAR_SECONDS = 31536000;

/**
 * Read the program cookie on the client. Returns the raw slug, or null when
 * absent/undecodable. Validation against the active program list happens
 * server-side in `resolveViewerScope`.
 */
export function readProgramCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${PROGRAM_COOKIE}=`));
  if (!match) return null;
  try {
    const value = decodeURIComponent(match.slice(PROGRAM_COOKIE.length + 1));
    return value.length > 0 ? value : null;
  } catch {
    // A malformed cookie value (e.g. a stray `%`) makes decodeURIComponent
    // throw; treat it as "no program" rather than crashing the caller.
    return null;
  }
}

/**
 * Write the program cookie on the client. `path=/` so every route sees it,
 * 1-year max-age, SameSite=Lax. `Secure` only over https so the cookie is not
 * silently dropped in local development.
 */
export function writeProgramCookie(slug: string): void {
  if (typeof document === 'undefined') return;
  const secure =
    typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${PROGRAM_COOKIE}=${encodeURIComponent(slug)}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;
}
